import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Loader2, Plus, Search, SlidersHorizontal, X } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { SwipeRoot } from '@/components/ui/swipe-actions';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { devicesService } from '../../services/devices';
import { isNewClimb } from '../../services/gymVisits';
import { localStorageService } from '../../services/localStorage';
import type { Climb } from '../../types/climb';
import type { Board } from '../../types/device';
import BoardFiltersDialog from './BoardFiltersDialog';
import {
  activeFilterCount,
  boardClimbsQuery,
  boardFilterChips,
  boardFiltersStorageKey,
  boardSearchStorageKey,
  DEFAULT_BOARD_FILTERS,
  normalizeBoardFilters,
  type BoardFilters,
} from './board-filters';
import ClimbRow from './ClimbRow';
import CurrentBoardProblem from './CurrentBoardProblem';

interface BoardTabContentProps {
  board: Board;
  gymSlug: string;
  isStaffAdmin: boolean;
  loggedIn: boolean;
  filterKey: string;
  returnPath?: string;
  saveFilteredClimbs: (climbs: Climb[], index?: number) => void;
  /** What "new" is measured against on this visit; see ClimbsList, which hands it down. */
  newSince?: Date | null;
}

const PER_PAGE = 25;
const SEARCH_DEBOUNCE_MS = 300;

/**
 * The climbs on one board, shown under its tab on the gym profile. A gym board
 * lists its own climbs; a global board (Kilter/Moonboard) lists the shared,
 * worldwide pool for its type, narrowed to the hold sets this wall was built
 * from. Clicking a climb saves the list for swipe navigation, exactly like the
 * boulder/route lists do.
 *
 * The list is paged and appended as you scroll: a Moonboard's library runs to
 * hundreds of problems, which is why the filters and the ordering are the
 * server's job here rather than something applied to an already-loaded array.
 * Every change of filter or sort starts the list again from page one.
 *
 * Only the name search sits on the toolbar; the rest live behind the filters
 * button and are remembered per board, so coming back to a wall you were
 * working through does not mean setting them all again.
 */
export default function BoardTabContent({
  board,
  gymSlug,
  isStaffAdmin,
  loggedIn,
  filterKey,
  returnPath,
  saveFilteredClimbs,
  newSince,
}: BoardTabContentProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [climbs, setClimbs] = useState<Climb[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [inclinations, setInclinations] = useState<number[]>([]);
  const [filtersOpen, setFiltersOpen] = useState(false);

  const [nameInput, setNameInput] = useState('');
  const [name, setName] = useState('');
  const [filters, setFilters] = useState<BoardFilters>(() =>
    normalizeBoardFilters(localStorageService.get<Partial<BoardFilters>>(boardFiltersStorageKey(board.id)))
  );

  // The name is deliberately not remembered: coming back to a half-typed search
  // and an apparently empty board is a puzzle, not a convenience.
  const applyFilters = (next: BoardFilters) => {
    setFilters(next);
    localStorageService.set(boardFiltersStorageKey(board.id), next);
  };

  // Typing a name should not fire a request per keystroke.
  useEffect(() => {
    const timer = setTimeout(() => setName(nameInput.trim()), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [nameInput]);

  const grades = board.grading_system?.grades ?? [];
  // Which sets are bolted on changes which problems exist, so it belongs in the
  // query key; joined so a fresh array with the same sets does not refetch.
  const holdSetsKey = (board.resolved_hold_sets ?? []).join(',');

  const query = useCallback(
    (page: number) => ({ page, per_page: PER_PAGE, ...boardClimbsQuery(filters, name) }),
    [name, filters]
  );

  // Which page the scroll has reached. A ref because loadMore reads it at the
  // moment it fires, not at the moment it was built.
  const pageRef = useRef(1);

  // First page, and every time a filter or the ordering changes.
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    pageRef.current = 1;

    devicesService
      .listBoardClimbs(gymSlug, board.id, query(1))
      .then((page) => {
        if (cancelled) return;
        setClimbs(page.climbs);
        setHasMore(page.hasMore);
        setInclinations(page.inclinations);
      })
      .catch((error) => {
        console.error('Failed to fetch board climbs:', error);
        if (!cancelled) {
          setClimbs([]);
          setHasMore(false);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [gymSlug, board.id, holdSetsKey, query]);

  // The scroll sentinel can fire again before a page lands, so the guard is a
  // ref rather than state: it has to be true the moment the request goes out.
  const loadingMoreRef = useRef(false);
  const loadMore = useCallback(async () => {
    if (loadingMoreRef.current) return;
    loadingMoreRef.current = true;
    setLoadingMore(true);

    try {
      const nextPage = pageRef.current + 1;
      const page = await devicesService.listBoardClimbs(gymSlug, board.id, query(nextPage));
      pageRef.current = nextPage;
      setClimbs((prev) => [...prev, ...page.climbs]);
      setHasMore(page.hasMore);
    } catch (error) {
      console.error('Failed to fetch more board climbs:', error);
      setHasMore(false);
    } finally {
      loadingMoreRef.current = false;
      setLoadingMore(false);
    }
  }, [gymSlug, board.id, query]);

  // Kept in a ref so the observer below survives a new loadMore identity.
  const loadMoreRef = useRef(loadMore);
  useEffect(() => {
    loadMoreRef.current = loadMore;
  });

  const sentinelRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel || !hasMore || loading) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) loadMoreRef.current();
      },
      { threshold: 0.1 }
    );
    observer.observe(sentinel);

    return () => observer.disconnect();
  }, [hasMore, loading]);

  const filterCount = activeFilterCount(filters);
  const anythingApplied = name !== '' || filterCount > 0;
  const angleOptions = board.adjustable_inclination ? inclinations : [];
  const chips = boardFilterChips(filters, grades);

  const openClimb = (climb: Climb) => {
    const index = climbs.findIndex((c) => c.id === climb.id);
    saveFilteredClimbs(climbs, index);
    sessionStorage.setItem(`last_viewed_climb_${filterKey}`, climb.slug);
    // So the problem's page can list what this list shows (BoardClimbsListDrawer).
    sessionStorage.setItem(boardSearchStorageKey(board.id), name);
    if (returnPath) sessionStorage.setItem(`climb_return_path_${climb.slug}`, returnPath);
    navigate(`/gyms/${gymSlug}/boards/${board.id}/climbs/${climb.slug}`, { state: { climb } });
  };

  const goCreate = () => navigate(`/gyms/${gymSlug}/boards/${board.id}/climbs/new`);

  const removeClimb = (climbId: string) =>
    setClimbs((prev) => prev.filter((c) => c.id !== climbId));
  const replaceClimb = (updated: Climb) =>
    setClimbs((prev) => prev.map((c) => (c.id === updated.id ? updated : c)));

  return (
    <>
      {/* What is lit on the wall right now, above the list of what could be. */}
      <CurrentBoardProblem gymSlug={gymSlug} boardId={board.id} />

      <div className="mb-3 flex items-center gap-2">
        <div className="relative flex-1">
          <Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={nameInput}
            onChange={(e) => setNameInput(e.target.value)}
            placeholder={t('climbs.board.searchPlaceholder')}
            aria-label={t('climbs.board.searchPlaceholder')}
            className="pl-9"
          />
        </div>

        <Button variant="outline" onClick={() => setFiltersOpen(true)}>
          <SlidersHorizontal className="size-4 sm:mr-2" />
          <span className="hidden sm:inline">{t('climbs.board.filters')}</span>
          {filterCount > 0 && (
            <span className="ml-2 flex size-5 items-center justify-center rounded-full bg-primary text-xs text-primary-foreground">
              {filterCount}
            </span>
          )}
        </Button>
      </div>

      <BoardFiltersDialog
        open={filtersOpen}
        onOpenChange={setFiltersOpen}
        filters={filters}
        onApply={applyFilters}
        grades={grades}
        inclinations={angleOptions}
        loggedIn={loggedIn}
      />

      {/* What is in force, since the filters themselves are behind the dialog.
          Same chips the boulder and route lists show. */}
      {chips.length > 0 && (
        <div className="mb-3 flex flex-wrap items-center gap-2">
          {chips.map((chip) => (
            <button
              key={chip.key}
              onClick={() => applyFilters({ ...filters, ...chip.cleared })}
              className="inline-flex items-center gap-1.5 rounded-md bg-primary/10 px-2.5 py-1 text-xs font-medium text-primary transition-colors hover:bg-primary/20"
            >
              <span className="text-muted-foreground">{t(`climbs.board.chips.${chip.key}`)}:</span>
              <span>{chip.valueKey ? t(chip.valueKey) : chip.value}</span>
              <X className="ml-0.5 size-3" />
            </button>
          ))}
          {chips.length > 1 && (
            <button
              onClick={() => applyFilters(DEFAULT_BOARD_FILTERS)}
              className="text-xs text-muted-foreground underline hover:text-foreground"
            >
              {t('climbs.clearAllFilters')}
            </button>
          )}
        </div>
      )}

      <Card>
        {/* One list for the rows' swipe strips, so opening one closes the rest. */}
        <SwipeRoot render={<CardContent className="p-0" />}>
          {loading ? (
            <div className="p-12 text-center text-muted-foreground">{t('common.loading')}</div>
          ) : climbs.length > 0 ? (
            <>
              {climbs.map((climb) => (
                <ClimbRow
                  key={climb.id}
                  climb={climb}
                  isStaffAdmin={isStaffAdmin}
                  onClimbClick={openClimb}
                  onClimbUpdated={replaceClimb}
                  onClimbDeleted={removeClimb}
                  board={board}
                  showInclination={board.adjustable_inclination}
                  isNew={newSince !== undefined && isNewClimb(climb.created_at, newSince)}
                />
              ))}

              {hasMore && (
                <div ref={sentinelRef} className="flex justify-center p-4" data-testid="board-climbs-sentinel">
                  {loadingMore && <Loader2 className="size-5 animate-spin text-muted-foreground" />}
                </div>
              )}
            </>
          ) : (
            <div className="p-12 text-center">
              <p className="mb-4 text-muted-foreground">
                {anythingApplied ? t('climbs.board.noMatches') : t('climbs.boardEmpty')}
              </p>
              {loggedIn && !anythingApplied && (
                <Button onClick={goCreate}>
                  <Plus className="mr-2 size-4" />
                  {t('climbs.createFirstBoardClimb')}
                </Button>
              )}
            </div>
          )}
        </SwipeRoot>
      </Card>

      {loggedIn && climbs.length > 0 && (
        <button
          onClick={goCreate}
          aria-label={t('climbs.addBoardClimb')}
          className="fixed bottom-20 right-4 z-30 flex size-14 items-center justify-center rounded-full bg-primary text-primary-foreground shadow-lg transition-colors hover:bg-primary/90 md:bottom-6"
        >
          <Plus className="size-6" />
        </button>
      )}
    </>
  );
}
